/**
 * AdrenalineSkillEffect — Specter wraith-path Endurance passive: dash-attack stamina refund.
 *
 * Landing a dash-attack refills 30% of the stamina bar. The aggressive option
 * feeds the resource economy — commit to damage mid-dash and get rewarded
 * with stamina instead of punished for spending it. Always on once unlocked.
 *
 * On equip: sets `dashAttackStaminaRefundRatio` on StaminaComponent.
 * On unequip: resets `dashAttackStaminaRefundRatio` to 0.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-drift-path-proposal.md § Adrenaline
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface AdrenalineDeps {
  readonly stamina: {
    /** Fraction of max stamina refunded on dash-attack hit (0 = disabled, 0.3 = 30%). */
    dashAttackStaminaRefundRatio: number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface AdrenalineConfig {
  /** Fraction of max stamina refunded on dash-attack hit (e.g. 0.3 = 30%). */
  readonly dashAttackStaminaRefundRatio: number;
}

const DEFAULT_CONFIG: AdrenalineConfig = {
  dashAttackStaminaRefundRatio: 0.3,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class AdrenalineSkillEffect implements SkillEffect {
  readonly skillId = 'adrenaline';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;
  readonly staminaCost: number = 0;

  private readonly deps: AdrenalineDeps;
  private readonly config: AdrenalineConfig;

  /** Whether the refund ratio has been applied (guards against double-application). */
  private _applied = false;

  constructor(deps: AdrenalineDeps, config: Partial<AdrenalineConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  deactivate(): void {}
  isActive(): boolean { return false; }

  update(_delta: number): void {
    // Apply the dash-attack stamina refund ratio on first update tick (equip).
    if (!this._applied) {
      this.deps.stamina.dashAttackStaminaRefundRatio = this.config.dashAttackStaminaRefundRatio;
      this._applied = true;
    }
  }

  destroy(): void {
    // Reset refund ratio to neutral on unequip
    if (this._applied) {
      this.deps.stamina.dashAttackStaminaRefundRatio = 0;
      this._applied = false;
    }
  }
}
